import type { Schemas } from '@argzombie/api-client';

export type Tokens = Schemas['AuthTokens'];

const KEY = 'argzombie.admin.session';
const STAFF_ROLES = ['Admin', 'Designer'];

type Listener = (tokens: Tokens | null) => void;

/**
 * The admin session: tokens in sessionStorage (they die with the tab; this is an internal tool without a server of
 * its own). The API enforces every permission; this only decides which screen to show.
 */
export class Session {
  private tokens: Tokens | null;
  private readonly listeners = new Set<Listener>();
  private refreshing: Promise<boolean> | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly storage: Storage | null = typeof sessionStorage === 'undefined'
      ? null
      : sessionStorage,
  ) {
    this.tokens = this.load();
  }

  get current(): Tokens | null {
    return this.tokens;
  }

  get isStaff(): boolean {
    return this.tokens?.roles.some((r) => STAFF_ROLES.includes(r)) ?? false;
  }

  get isAdmin(): boolean {
    return this.tokens?.roles.includes('Admin') ?? false;
  }

  accessToken = (): string | null => this.tokens?.accessToken ?? null;

  set(tokens: Tokens | null) {
    this.tokens = tokens;
    if (tokens) this.storage?.setItem(KEY, JSON.stringify(tokens));
    else this.storage?.removeItem(KEY);
    this.listeners.forEach((l) => l(tokens));
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Rotates the refresh token. Signs out when the API refuses it. */
  refresh(): Promise<boolean> {
    if (!this.tokens) return Promise.resolve(false);
    this.refreshing ??= (async () => {
      try {
        const res = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.tokens?.refreshToken }),
        });
        if (!res.ok) {
          this.set(null);
          return false;
        }
        this.set((await res.json()) as Tokens);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  /** fetch that retries once with fresh tokens after a 401. Given to the API client. */
  fetch = async (input: Request): Promise<Response> => {
    const retry = input.clone();
    const res = await fetch(input);
    if (res.status !== 401 || !this.tokens) return res;
    if (!(await this.refresh())) return res;
    retry.headers.set('Authorization', `Bearer ${this.tokens.accessToken}`);
    return fetch(retry);
  };

  private load(): Tokens | null {
    try {
      const raw = this.storage?.getItem(KEY);
      return raw ? (JSON.parse(raw) as Tokens) : null;
    } catch {
      return null;
    }
  }
}
